import { Prisma } from '@prisma/client';

import { ParseError } from '../../errors/parse-error';
import * as guards from './guards';
import * as AppTypes from './app-types';
import * as UserTypes from './user-types';
import { BranchForSubscription, BranchWithParsedCases } from './branch-types';
import { PaymentRedisRecord, PersonalDataForReceipt } from './payment-types';

export const parseString = (value: unknown): string => {
  if (guards.isString(value)) return value;
  throw new ParseError('Объект не соответствует типу string');
};

export const parseNumber = (value: unknown): number => {
  if (guards.isNumber(value)) return value;
  throw new ParseError('Объект не соответствует типу number');
};

export const parseBoolean = (value: unknown): boolean => {
  if (guards.isBoolean(value)) return value;
  throw new ParseError('Объект не соответствует типу boolean');
};

export const parseStringArray = (values: unknown): string[] => {
  if (!Array.isArray(values))
    throw new ParseError('Объект не соответствует типу string[]');

  return values
    .map((item: unknown) => {
      if (guards.isString(item)) {
        return item;
      }

      return null;
    })
    .filter((item): item is string => item !== null); // Отфильтровываем некорректные объекты
};

export const parseDbUserPersonalData = (
  obj: unknown
): AppTypes.DbUserPersonalData => {
  if (guards.isDbUserPersonalData(obj)) return obj;
  throw new ParseError('Объект не соответствует типу DbUserPersonalData');
};

export const parsePublicUserPersonalData = (
  obj: unknown
): AppTypes.PublicUserPersonalData => {
  if (guards.isPublicUserPersonalData(obj)) return obj;
  throw new ParseError('Объект не соответствует типу PublicUserPersonalData');
};

export const parseUserPreferences = (
  obj: unknown
): AppTypes.UserPreferences => {
  if (guards.isUserPreferences(obj)) return obj;
  throw new ParseError('Объект не соответствует типу UserPreferences');
};

export const parseUserClient = (obj: unknown): AppTypes.UserClient => {
  if (guards.isUserClient(obj)) return obj;
  throw new ParseError('Объект не соответствует типу UserClient');
};

export const parseSubscriptionDetails = (
  obj: unknown
): AppTypes.SubscriptionDetails => {
  if (guards.isSubscriptionDetails(obj)) return obj;
  throw new ParseError('Объект не соответствует типу SubscriptionDetails');
};

export const parseSubscription = (obj: unknown): AppTypes.Subscription => {
  if (guards.isSubscription(obj)) return obj;
  throw new ParseError('Объект не соответствует типу Subscription');
};

export const parseVKData = (obj: unknown): AppTypes.VKData => {
  if (guards.isVKData(obj)) return obj;
  throw new ParseError('Объект не соответствует типу VKData');
};

export const parseRegistrationData = (
  obj: unknown
): AppTypes.RegistrationData => {
  if (guards.isRegistrationData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу RegistrationData');
};

export const parseSignUpSessionForClient = (
  obj: unknown
): AppTypes.SignUpSessionForClient => {
  if (guards.isSignUpSessionForClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SignUpSessionForClient');
};

export const parseSignUpSessionForRedisIncomingData = (
  obj: unknown
): AppTypes.SignUpSessionForRedisIncomingData => {
  if (guards.isSignUpSessionForRedisIncomingData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу SignUpSessionForRedisIncomingData'
  );
};

export const parseSessionClient = (obj: unknown): AppTypes.SessionClient => {
  if (guards.isSessionClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SessionClient');
};

export const parseSessionClientFromUserService = (
  obj: unknown
): AppTypes.SessionClientFromUserService => {
  if (guards.isSessionClientFromUserService(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу SessionClientFromUserService'
  );
};

export const parseConfigUrlData = (obj: unknown): AppTypes.ConfigUrlData => {
  if (guards.isConfigUrlData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу ConfigUrlData');
};

export const parsePaginationData = (obj: unknown): AppTypes.PaginationData => {
  if (guards.isPaginationData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу PaginationData');
};

export const parseCreateUserArgs = (obj: unknown): UserTypes.CreateUserArgs => {
  if (guards.isCreateUserArgs(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу CreateUserArgs');
};

export const parseUpdatePersonalDataArgs = (
  obj: unknown
): UserTypes.UpdatePersonalDataArgs => {
  if (guards.isUpdatePersonalDataArgs(obj)) {
    return obj;
  }
  console.error(`Объект не соответствует типу UpdatePersonalDataArgs: `, obj);
  throw new ParseError('Объект не соответствует типу UpdatePersonalDataArgs');
};

export const parseBranchWithParsedCasesArray = (
  obj: unknown
): BranchWithParsedCases[] => {
  if (!obj) return [];
  if (guards.isBranchWithParsedCasesArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу BranchWithParsedCases[]');
};

export const parseCases = (obj: unknown): AppTypes.Cases => {
  if (guards.isCases(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу isCases');
};

export const parseBranchForSubscriptionArray = (
  obj: unknown
): BranchForSubscription[] => {
  if (guards.isBranchForSubscriptionArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу BranchForSubscription[]');
};

export const parseAllInSubscriptionTableData = (
  obj: unknown
): AppTypes.AllInSubscriptionTableData => {
  if (guards.isAllInSubscriptionTableData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу isAllInSubscriptionTableData'
  );
};

export const parseCustomSubscriptionTableData = (
  obj: unknown
): AppTypes.CustomSubscriptionTableData => {
  if (guards.isCustomSubscriptionTableData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу CustomSubscriptionTableData'
  );
};

export const parseProtectedRoute = (
  value: unknown
): AppTypes.ProtectedRoute => {
  if (value === 'premium' || value === 'myaccount') {
    return value;
  }
  return undefined;
};

export const parseMetadataClient = (obj: unknown): AppTypes.MetadataClient => {
  if (guards.isMetadataClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу MetadataClient');
};

export const parseOpenGraphImagesDB = (
  obj: unknown
): AppTypes.OpenGraphImageDB[] => {
  if (guards.isOpenGraphImageDBArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу isOpenGraphDB');
};

export const jsonValueToOpenGraphDB = (
  openGraph: Prisma.JsonValue
): AppTypes.OpenGraphDB => {
  if (guards.isOpenGraphDB(openGraph)) {
    return openGraph;
  }
  return {} as AppTypes.OpenGraphDB; // Возвращаем пустой объект, если данные невалидные
};

export const parsePaymentCreateInput = (
  obj: unknown
): Prisma.PaymentCreateInput => {
  if (guards.isPaymentCreateInput(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу Prisma.PaymentCreateInput'
  );
};

export const parsePaymentRedisRecordsArray = (
  obj: unknown
): PaymentRedisRecord[] => {
  if (guards.isPaymentRedisRecordsArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу PaymentRedisRecord[]');
};

export const parsePersonalDataForReceipt = (
  obj: unknown
): PersonalDataForReceipt => {
  if (guards.isPersonalDataForReceipt(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу PersonalDataForReceipt');
};
